import {
  AutoCompleteUncontrolled,
  DatePickerUncontrolled,
  DateRangeSelect,
  ISelectType,
  ModalFilters,
} from "@4uhub/lib4uhub";
import { Box, Stack, TextField } from "@mui/material";
import React, {
  Dispatch,
  SetStateAction,
  memo,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import FiltersActions from "../../../../components/FiltersActions/FiltersActions";
import useFilter from "../../../../hooks/useFilter";
import { LIST_STATUS_VALUES, TYPE_OF_APPOINTMENT_VALUES } from "./utils";

export interface IFilterParams {
  Document: string;
  CreatedStart: Date | null;
  CreatedEnd: Date | null;
  TypeOfAppointment: string;
  Status: string;
}

interface IMacroFilterProps {
  onFilterParams: Dispatch<SetStateAction<IFilterParams>>;
  filterParams: IFilterParams;
}

const translationPath = "page.4uBus.logs.calendarControl.";

const fieldWrapperSx = {
  flex: "1 1 100%",
  minWidth: 0,
};

const MacroFilter: React.FC<IMacroFilterProps> = ({
  onFilterParams,
  filterParams,
}) => {
  const { filter, showCleanBtn, initialState, setFilter, cleanFilter } =
    useFilter(filterParams);
  const { t } = useTranslation();

  const [resetDateRange, setResetDateRange] = useState(false);
  const [enablePeriodFields, setEnablePeriodFields] = useState(false);

  const typeOfAppointmentOptions = useMemo<ISelectType[]>(
    () =>
      TYPE_OF_APPOINTMENT_VALUES.map((value) => ({
        id: value,
        name: t(translationPath + `type_of_appointment_values.${value}`) || value,
      })),
    [t],
  );

  const statusOptions = useMemo<ISelectType[]>(
    () =>
      LIST_STATUS_VALUES.map((value) => ({
        id: value,
        name: t(translationPath + `status_values.${value}`) || value,
      })),
    [t],
  );

  const selectedTypeOfAppointment = useMemo(
    () =>
      typeOfAppointmentOptions.find((o) => o.id === filter.TypeOfAppointment) ??
      null,
    [typeOfAppointmentOptions, filter.TypeOfAppointment],
  );

  const selectedStatus = useMemo(
    () => statusOptions.find((o) => o.id === filter.Status) ?? null,
    [statusOptions, filter.Status],
  );

  const badgeCount = useMemo(() => {
    const initial = initialState.current;
    let count = 0;

    if (filterParams.TypeOfAppointment !== initial.TypeOfAppointment) count += 1;
    if (filterParams.Status !== initial.Status) count += 1;

    return count;
  }, [filterParams, initialState]);

  const filterHandler = () => {
    onFilterParams(filter);
  };

  const cleanHandler = () => {
    onFilterParams(initialState.current);
    cleanFilter();
    setResetDateRange((prev) => !prev);
  };

  return (
    <Stack spacing={0.75} sx={{ width: "100%" }}>
      <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
        <DateRangeSelect
          setStartDate={(date) => setFilter({ CreatedStart: date })}
          setEndDate={(date) => setFilter({ CreatedEnd: date })}
          // A lib chama setEnableFields(false) quando a opção é "Selecionar período" —
          // é exatamente quando os campos de data devem ficar editáveis.
          setEnableFields={(enable) => setEnablePeriodFields(!enable)}
          resetToToday={resetDateRange}
        />

        <Box sx={{ flex: "0 0 160px" }}>
          <DatePickerUncontrolled
            value={filter.CreatedStart}
            onChange={(date) => setFilter({ CreatedStart: date })}
            label={t(translationPath + "created_start") || "Data inicial"}
            isClearable
            disable={!enablePeriodFields}
          />
        </Box>

        <Box sx={{ flex: "0 0 160px" }}>
          <DatePickerUncontrolled
            value={filter.CreatedEnd}
            onChange={(date) => setFilter({ CreatedEnd: date })}
            label={t(translationPath + "created_end") || "Data final"}
            isClearable
            disable={!enablePeriodFields}
            minDate={filter.CreatedStart ?? undefined}
          />
        </Box>

        <Box sx={{ flex: "1 1 200px", minWidth: 180 }}>
          <TextField
            value={filter.Document}
            size="small"
            label={t(translationPath + "document") || "CPF"}
            fullWidth
            onChange={(e) =>
              setFilter({ Document: e.target.value.replace(/\D/g, "") })
            }
            inputProps={{ inputMode: "numeric", maxLength: 14 }}
          />
        </Box>

        <FiltersActions
          onFilter={filterHandler}
          onClean={cleanHandler}
          showCleanBtn={showCleanBtn}
        />

        <ModalFilters
          badgeCount={badgeCount}
          actionsDisabled={false}
          onFilter={filterHandler}
          onCleanFilter={cleanHandler}
          triggerVariant="button"
          triggerLabel={t(translationPath + "additionalFilters") || "Mais filtros"}
          triggerButtonVariant="contained"
        >
          <Stack
            direction="row"
            gap={1.5}
            alignItems="flex-start"
            flexWrap="wrap"
            sx={{ width: "100%" }}
          >
            <Box sx={fieldWrapperSx}>
              <AutoCompleteUncontrolled
                label={t(translationPath + "type_of_appointment") || "Tipo de agenda"}
                onValueSelected={(value) =>
                  setFilter({ TypeOfAppointment: value ? String(value.id) : "" })
                }
                value={selectedTypeOfAppointment}
                options={typeOfAppointmentOptions}
                size="small"
                fullWidth
              />
            </Box>

            <Box sx={fieldWrapperSx}>
              <AutoCompleteUncontrolled
                label={t(translationPath + "status") || "Status da lista"}
                onValueSelected={(value) =>
                  setFilter({ Status: value ? String(value.id) : "" })
                }
                value={selectedStatus}
                options={statusOptions}
                size="small"
                fullWidth
              />
            </Box>
          </Stack>
        </ModalFilters>
      </Stack>
    </Stack>
  );
};

export default memo(MacroFilter);
